import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Input,
  Select,
  Button,
  Tag,
  Space,
  Image,
  Tooltip,
  Typography,
  Tabs,
  Modal,
  Popconfirm,
  message,
} from "antd";
import {
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  UndoOutlined,
  StarFilled,
  WarningOutlined,
} from "@ant-design/icons";
import axios from "axios";
import "../../../config";

const { Search, TextArea } = Input;
const { Text } = Typography;

const FALLBACK_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='44' height='44'%3E%3Crect width='44' height='44' fill='%23f0f0f0'/%3E%3C/svg%3E";

const API = () => global.config.APIURL.BaseURL + "api/OnlineSellerProduct/";
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// "dd-MM-yyyy"
const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${date.getFullYear()}`;
};

const StatusDot = ({ active, label, activeColor = "#52c41a" }) => (
  <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
    <span
      style={{
        width: 7,
        height: 7,
        borderRadius: "50%",
        background: active ? activeColor : "#d9d9d9",
        flexShrink: 0,
        display: "inline-block",
      }}
    />
    <Text style={{ fontSize: 12, color: active ? "#262626" : "#bfbfbf" }}>{label}</Text>
  </span>
);

const ProductCell = ({ record, warnings }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
    <Image
      src={record.ImageUrl}
      width={44}
      height={44}
      style={{ borderRadius: 8, objectFit: "cover", flexShrink: 0, border: "1px solid #f0f0f0" }}
      fallback={FALLBACK_IMG}
      preview={false}
    />
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontWeight: 600,
          fontSize: 13,
          color: "#141414",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 240,
        }}
      >
        {record.ProductName}
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {record.DesignNo || "—"}
      </Text>
      {warnings}
    </div>
  </div>
);

const CategoryTags = ({ cats = [] }) => {
  const visible = cats.slice(0, 2);
  const extra = cats.length - 2;
  return (
    <Space wrap size={4}>
      {visible.map((cat, i) => (
        <Tag key={i} color="geekblue" style={{ fontSize: 11, margin: 0 }}>
          {cat}
        </Tag>
      ))}
      {extra > 0 && (
        <Tooltip title={cats.slice(2).join(", ")}>
          <Tag style={{ fontSize: 11, margin: 0 }}>+{extra}</Tag>
        </Tooltip>
      )}
    </Space>
  );
};

// ---------------------------------------------------------------- Add products popup
const AddProductsModal = ({ open, onClose, onAdded, messageApi }) => {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [listed, setListed] = useState({ active: new Set(), removed: new Set() });
  const pageSize = 10;

  // Reuses the existing admin product search; only products active in the ERP can be added.
  const fetchProducts = useCallback(async (pageNumber, searchText) => {
    setLoading(true);
    try {
      let url =
        global.config.APIURL.BaseURL +
        `api/ProductAdminEdit/GetAllProductsForAdmin?pageNumber=${pageNumber}&pageSize=${pageSize}&isActive=true`;
      if (searchText) url += `&searchText=${encodeURIComponent(searchText)}`;
      const res = await axios.get(url, authHeaders());
      setProducts(res.data.Products || []);
      setTotal(res.data.TotalCount || 0);
      setPage(pageNumber);
    } catch {
      messageApi.error("Failed to load products.");
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    if (!open) return;
    setSelectedKeys([]);
    setSearch("");
    fetchProducts(1, "");
    axios
      .get(API() + "GetListedProductIDs", authHeaders())
      .then((res) =>
        setListed({ active: new Set(res.data.Active || []), removed: new Set(res.data.Removed || []) })
      )
      .catch(() => {});
  }, [open, fetchProducts]);

  const handleAdd = async () => {
    setSaving(true);
    try {
      const res = await axios.post(API() + "AddProducts", { ProductIDs: selectedKeys }, authHeaders());
      const { Added = 0, Reactivated = 0 } = res.data || {};
      const parts = [];
      if (Added) parts.push(`${Added} added`);
      if (Reactivated) parts.push(`${Reactivated} restored`);
      messageApi.success((parts.join(", ") || "No changes") + " to the online seller list");
      onAdded();
      onClose();
    } catch {
      messageApi.error("Failed to add products.");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Product",
      key: "product",
      render: (_, record) => <ProductCell record={record} />,
    },
    {
      title: "Categories",
      dataIndex: "Categories",
      key: "Categories",
      render: (cats) => <CategoryTags cats={cats} />,
    },
    {
      title: "Stock",
      key: "stock",
      width: 110,
      render: (_, r) => (
        <StatusDot active={r.IsInStock} label={r.IsInStock ? "In Stock" : "Out of Stock"} />
      ),
    },
    {
      title: "List status",
      key: "listStatus",
      width: 190,
      render: (_, r) =>
        listed.active.has(r.ProductID) ? (
          <Tag color="green">On list ✓</Tag>
        ) : listed.removed.has(r.ProductID) ? (
          <Tooltip title="Adding it again restores it to the list">
            <Tag color="red">Discontinued</Tag>
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={900}
      title="Add products to online seller list"
      destroyOnClose
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Text type="secondary">{selectedKeys.length} selected</Text>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" disabled={!selectedKeys.length} loading={saving} onClick={handleAdd}>
              Add {selectedKeys.length || ""} product{selectedKeys.length === 1 ? "" : "s"}
            </Button>
          </Space>
        </div>
      }
    >
      <Search
        placeholder="Search design no / name"
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onSearch={(value) => fetchProducts(1, value)}
        style={{ marginBottom: 12 }}
      />
      <Table
        rowKey="ProductID"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={products}
        scroll={{ x: 640 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: false,
          size: "small",
          onChange: (p) => fetchProducts(p, search),
        }}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: setSelectedKeys,
          preserveSelectedRowKeys: true,
          getCheckboxProps: (r) => ({ disabled: listed.active.has(r.ProductID) }),
        }}
      />
    </Modal>
  );
};

// ---------------------------------------------------------------- Page
const ManageOnlineSellerProducts = () => {
  // Hook-based messages: the static message.* API renders nothing under React 19 without antd's patch.
  const [messageApi, contextHolder] = message.useMessage();
  const [tab, setTab] = useState("active");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ OnListCount: 0, RemovedCount: 0 });
  const [visibleDays, setVisibleDays] = useState(90);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [categoryID, setCategoryID] = useState(0);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [removeTargets, setRemoveTargets] = useState(null);
  const [removeNote, setRemoveNote] = useState("");
  const [removing, setRemoving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const fetchList = useCallback(async (status, page, pageSize, search, catID) => {
    setLoading(true);
    try {
      let url =
        API() +
        `GetListForAdmin?status=${status}&pageNumber=${page}&pageSize=${pageSize}&categoryID=${catID}`;
      if (search) url += `&searchText=${encodeURIComponent(search)}`;
      const res = await axios.get(url, authHeaders());
      setItems(res.data.Items || []);
      setCounts({ OnListCount: res.data.OnListCount || 0, RemovedCount: res.data.RemovedCount || 0 });
      setVisibleDays(res.data.DiscontinuedVisibleDays || 90);
      setPagination({ current: page, pageSize, total: res.data.TotalCount || 0 });
    } catch (error) {
      if (error.response && error.response.status === 403) messageApi.error("You don't have access to this page.");
      else messageApi.error("Failed to load the online seller list.");
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  const reload = (page = 1) => fetchList(tab, page, pagination.pageSize, searchText, categoryID);

  useEffect(() => {
    fetchList("active", 1, 20, "", 0);
    axios
      .get(global.config.APIURL.BaseURL + "api/ProductCategory/ListofProductCategory", authHeaders())
      .then((res) => {
        const flat = (res.data || []).flatMap((group) => group.data || []);
        flat.sort((a, b) => a.CategoryName.localeCompare(b.CategoryName));
        setCategories(flat);
      })
      .catch(() => {});
  }, [fetchList]);

  const handleTabChange = (key) => {
    setTab(key);
    setSelectedKeys([]);
    fetchList(key, 1, pagination.pageSize, searchText, categoryID);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchList(tab, 1, pagination.pageSize, value, categoryID);
  };

  const handleCategory = (value) => {
    setCategoryID(value);
    fetchList(tab, 1, pagination.pageSize, searchText, value);
  };

  const handleTableChange = (pag) => fetchList(tab, pag.current, pag.pageSize, searchText, categoryID);

  const confirmRemove = async () => {
    setRemoving(true);
    try {
      const ids = removeTargets.map((r) => r.ProductID);
      await axios.post(API() + "RemoveProducts", { ProductIDs: ids, Reason: removeNote.trim() }, authHeaders());
      messageApi.success(`${ids.length} product${ids.length > 1 ? "s" : ""} moved to Discontinued`);
      setRemoveTargets(null);
      setRemoveNote("");
      setSelectedKeys([]);
      reload(pagination.current);
    } catch {
      messageApi.error("Failed to remove products.");
    } finally {
      setRemoving(false);
    }
  };

  const handleRestore = async (record) => {
    try {
      await axios.post(API() + "AddProducts", { ProductIDs: [record.ProductID] }, authHeaders());
      messageApi.success(`${record.DesignNo} restored to the online seller list`);
      reload(pagination.current);
    } catch {
      messageApi.error("Failed to restore product.");
    }
  };

  // Products on the list that sellers can't currently see, and why.
  const warnings = (record) => (
    <>
      {!record.IsProductActive && (
        <div>
          <Tooltip title="Switched off in the ERP, so sellers can't see it. Remove it if it's discontinued.">
            <Tag color="warning" icon={<WarningOutlined />} style={{ marginTop: 4 }}>
              Inactive in ERP
            </Tag>
          </Tooltip>
        </div>
      )}
      {record.IsProductActive && !record.IsPublished && (
        <div>
          <Tooltip title="Not published on the website, so sellers can't see it yet.">
            <Tag color="default" style={{ marginTop: 4 }}>
              Not published
            </Tag>
          </Tooltip>
        </div>
      )}
    </>
  );

  const onListColumns = [
    {
      title: "Product",
      key: "product",
      render: (_, record) => <ProductCell record={record} warnings={warnings(record)} />,
    },
    {
      title: "Categories",
      dataIndex: "Categories",
      key: "Categories",
      render: (cats) => <CategoryTags cats={cats} />,
    },
    {
      title: "Stock",
      key: "stock",
      width: 110,
      render: (_, r) => (
        <StatusDot active={r.IsInStock} label={r.IsInStock ? "In Stock" : "Out of Stock"} />
      ),
    },
    {
      title: "Added on",
      dataIndex: "AddedOn",
      key: "AddedOn",
      width: 110,
      render: formatDate,
    },
    {
      title: "",
      key: "action",
      width: 100,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Button danger size="small" icon={<DeleteOutlined />} onClick={() => setRemoveTargets([record])}>
          Remove
        </Button>
      ),
    },
  ];

  const removedColumns = [
    {
      title: "Product",
      key: "product",
      render: (_, record) => <ProductCell record={record} />,
    },
    {
      title: "Categories",
      dataIndex: "Categories",
      key: "Categories",
      render: (cats) => <CategoryTags cats={cats} />,
    },
    {
      title: "Removed on",
      dataIndex: "RemovedOn",
      key: "RemovedOn",
      width: 110,
      render: formatDate,
    },
    {
      title: "Note for sellers",
      dataIndex: "RemovedReason",
      key: "RemovedReason",
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Sellers see it",
      key: "visible",
      width: 150,
      render: (_, r) =>
        r.VisibleToSellersUntil && new Date(r.VisibleToSellersUntil) >= new Date() ? (
          <Tag color="blue">Until {formatDate(r.VisibleToSellersUntil)}</Tag>
        ) : (
          <Tag>No (over {visibleDays} days)</Tag>
        ),
    },
    {
      title: "",
      key: "action",
      width: 100,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Popconfirm
          title="Restore to the online seller list?"
          description="Sellers will see it under Available again."
          okText="Restore"
          onConfirm={() => handleRestore(record)}
        >
          <Button size="small" icon={<UndoOutlined />}>
            Restore
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
      {contextHolder}
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: "#f9f0ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <StarFilled style={{ color: "#722ed1", fontSize: 16 }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#141414", lineHeight: 1.3 }}>
              Online Seller List
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Products online sellers can list on their stores and reorder.
            </Text>
          </div>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
          Add products
        </Button>
      </div>

      <Tabs
        activeKey={tab}
        onChange={handleTabChange}
        items={[
          { key: "active", label: `On list (${counts.OnListCount})` },
          { key: "removed", label: `Discontinued (${counts.RemovedCount})` },
        ]}
      />

      {/* Filters */}
      <Space wrap size={8} style={{ marginBottom: 12 }}>
        <Search
          placeholder="Search design no / name"
          onSearch={handleSearch}
          allowClear
          style={{ width: 240 }}
        />
        <Select
          value={categoryID}
          onChange={handleCategory}
          style={{ width: 200 }}
          showSearch
          optionFilterProp="label"
          options={[
            { value: 0, label: "All categories" },
            ...categories.map((c) => ({ value: c.ProductCategoryID, label: c.CategoryName })),
          ]}
        />
        <Tooltip title="Refresh">
          <Button icon={<ReloadOutlined />} onClick={() => reload(pagination.current)} />
        </Tooltip>
      </Space>

      {tab === "active" && selectedKeys.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            background: "#e6f4ff",
            border: "1px solid #91caff",
            borderRadius: 8,
            padding: "6px 12px",
            marginBottom: 12,
          }}
        >
          <span>
            <b>{selectedKeys.length}</b> selected
            <Button type="link" size="small" onClick={() => setSelectedKeys([])}>
              Clear
            </Button>
          </span>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => setRemoveTargets(items.filter((r) => selectedKeys.includes(r.ProductID)))}
          >
            Remove selected
          </Button>
        </div>
      )}

      <Table
        dataSource={items}
        columns={tab === "active" ? onListColumns : removedColumns}
        rowKey="ProductID"
        loading={loading}
        size="middle"
        scroll={{ x: 760 }}
        rowSelection={
          tab === "active" ? { selectedRowKeys: selectedKeys, onChange: setSelectedKeys } : undefined
        }
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ["20", "50", "100"],
          showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
          size: "small",
        }}
        onChange={handleTableChange}
      />
      {tab === "removed" && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Sellers see discontinued products for {visibleDays} days after removal. This tab keeps the full history.
        </Text>
      )}

      {/* Remove (single or bulk) */}
      <Modal
        open={!!removeTargets}
        title="Remove from online seller list"
        okText={removeTargets && removeTargets.length > 1 ? `Remove ${removeTargets.length} products` : "Remove"}
        okButtonProps={{ danger: true }}
        confirmLoading={removing}
        onOk={confirmRemove}
        onCancel={() => {
          setRemoveTargets(null);
          setRemoveNote("");
        }}
        destroyOnClose
      >
        {removeTargets && (
          <>
            <div style={{ marginBottom: 8, maxHeight: 160, overflowY: "auto" }}>
              {removeTargets.map((r) => (
                <div key={r.ProductID}>
                  <b>{r.DesignNo}</b> · {r.ProductName}
                </div>
              ))}
            </div>
            <p style={{ color: "#595959" }}>
              {removeTargets.length > 1 ? "They move" : "It moves"} to "Discontinued". Online sellers will see{" "}
              {removeTargets.length > 1 ? "them" : "it"} in their Discontinued tab for {visibleDays} days.
            </p>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>Note for sellers (optional)</div>
            <TextArea
              rows={3}
              maxLength={250}
              showCount
              value={removeNote}
              onChange={(e) => setRemoveNote(e.target.value)}
              placeholder="e.g. Replaced by JB-140"
            />
          </>
        )}
      </Modal>

      <AddProductsModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={() => reload(1)}
        messageApi={messageApi}
      />
    </div>
  );
};

export default ManageOnlineSellerProducts;
